/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { ApiQuery } from '@app/core/http/api-query';
import { FormGroup } from '@angular/forms';
import { SuppliersService } from '@app/suppliers/suppliers/suppliers.service';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { Supplier } from '@app/suppliers/suppliers/suppliers.model';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { SuppliersFormService } from '@app/suppliers/suppliers/suppliers-form.service';
import { Logger } from '@app/core';

const log = new Logger('SupplierSelectComponent');

@Component({
  selector: 'app-supplier-select',
  templateUrl: './supplier-select.component.html',
  styleUrls: ['./supplier-select.component.scss'],
})
export class SupplierSelectComponent implements OnInit {
  data: Observable<Supplier[]>;
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() parentFormGroup: FormGroup;
  input$ = new Subject<string>();
  @Input() model: any = 'undefined';
  @Input() hideLabel = false;
  @Input() onlyActive = true;
  @Output() modelChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() select = new EventEmitter();
  lastResponse: any = null;
  @Input() passFormControlName = 'supplier_id';
  supplierFormControlName: any = this.passFormControlName.replace('_id', '');
  @Input() includes: string = null;
  @Input() isInModal = true;

  constructor(private supplierService: SuppliersService, private supplierFormService: SuppliersFormService) {}

  ngOnInit() {
    if (this.model === 'undefined') {
      this.supplierFormControlName = this.passFormControlName.replace('_id', '');

      const preSelectedSupplier = this.parentFormGroup.get(this.supplierFormControlName);
      const preSelectedSupplierId = this.parentFormGroup.get(this.passFormControlName);
      /**
       * Попълва избрания хотел на едит.
       * Ако има FormControl 'supplier' в parentFormGroup.
       * Ако няма ще се опита да го get-не по ID
       */
      if (preSelectedSupplier) {
        /*
         * Следи за промени в supplier FormControl-a и когато намери валиден Supplier спира да следи
         */
        const preselectedSupplierSubscriber = preSelectedSupplier.valueChanges.subscribe((supplier: any) => {
          if (supplier && supplier.name) {
            this.data = of([supplier]);
            preselectedSupplierSubscriber.unsubscribe();
          }
        });
      } else if (preSelectedSupplierId) {
        /*
         * Ако не е намерен supplier FormControl-a търси supplier_id FormControl и се опитва да get-не supplier-a по ID
         */
        const preselectedSupplierIdSubscriber = preSelectedSupplierId.valueChanges.subscribe((supplierId: any) => {
          if (supplierId) {
            this.loadSuppliersById(supplierId);
            preselectedSupplierIdSubscriber.unsubscribe();
          }
        });
      }
    } else if (this.model !== undefined && this.model !== null && this.model !== '') {
      this.data = of([this.model]);
    }
    this.supplierSearch();
  }

  // loadSuppliers(keyword?: string) {
  //   this.loader = true;
  //
  //   this.supplierService.index(this.apiQuery.addFilter('name', keyword)).subscribe((response: PaginatedResponse) => {
  //     this.data = of(response.data);
  //     this.loader = false;
  //   });
  // }

  loadSuppliersById(id: number) {
    const apiQuery = new ApiQuery();
    if (this.includes) {
      apiQuery.addIncludes(this.includes);
    }

    this.loader = true;
    this.supplierService.show(id, apiQuery).subscribe((supplier: Supplier) => {
      this.data = of([supplier]);
      this.loader = false;
    });
  }

  supplierSearch() {
    if (this.onlyActive) {
      this.apiQuery.addFilter('active', true);
    }

    if (this.includes) {
      this.apiQuery.addIncludes(this.includes);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.supplierService.index(this.apiQuery.addFilters({ name: term })).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.loader = false))
          );
        })
      )
      .subscribe((suppliers: PaginatedResponse) => {
        this.data = of(suppliers.data);
        this.lastResponse = suppliers;
        this.loader = false;
      });
  }

  onChange(supplier: any) {
    this.select.emit(supplier);
    if (this.parentFormGroup.get(this.supplierFormControlName)) {
      this.parentFormGroup.get(this.supplierFormControlName).patchValue(supplier);
    }
  }

  add(searchTerm: string) {
    this.supplierFormService.open(new Supplier().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadSuppliersById(response.id);
        this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
      },
      (err) => {
        log.error(err);
      }
    );
  }

  changeModel(event: any) {
    this.modelChange.emit(event);
  }

  edit() {
    if (this.parentFormGroup.get(this.passFormControlName).value) {
      this.supplierFormService
        .open(new Supplier().deserialize({ id: this.parentFormGroup.get(this.passFormControlName).value }))
        .then(
          (response) => {
            this.loadSuppliersById(response.id);
            this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
            if (this.parentFormGroup.get(this.passFormControlName)) {
              this.parentFormGroup.get(this.passFormControlName).patchValue(response);
            }
          },
          (err) => {
            log.error(err);
          }
        );
    }
  }
}
